import { useState } from 'react'
import { Box, Flex, VStack, SimpleGrid } from '@chakra-ui/react'
import { LuTruck, LuLayoutGrid, LuList } from 'react-icons/lu'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, Text, useT, toaster } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { transportistasService, companiesService } from '../../api/services'
import { ApiError } from '../../api/client'
import { getErrorMessage } from '../../api/errorCodes'
import { usePagedRequest } from '../../hooks'
import type { CarrierListItemResponse } from '../../api/models/response'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { useHasPermission } from '../../auth/PermissionContext'
import ConsentModal from '../../components/ConsentModal'

const MotionBox = motion.create(Box)

// Mapeo de RegistrationStatus (CompanyBusiness Transportista) → clave i18n + colores del badge.
const STATUS_STYLES: Record<number, { key: string; bg: string; color: string }> = {
  0: { key: 'draft', bg: 'rgba(100, 116, 139, 0.18)', color: '#64748B' },
  1: { key: 'pendingReview', bg: 'rgba(212, 154, 42, 0.2)', color: '#d49a2a' },
  2: { key: 'observed', bg: 'rgba(200, 68, 47, 0.18)', color: '#c8442f' },
  3: { key: 'approved', bg: 'rgba(42, 138, 135, 0.2)', color: '#2a8a87' },
}

export default function TransportistasPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const canCreate = useHasPermission('carrier.create')
  const canUpdate = useHasPermission('carrier.update')
  const canView = useHasPermission('carrier.view')

  const [search, setSearch] = useState('')
  const [showConsent, setShowConsent] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table')

  const {
    items: empresas,
    totalCount,
    isFetching: loading,
    error,
    refresh,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
  } = usePagedRequest(transportistasService.getAll, {
    params: { search: search.trim() || undefined },
  })

  // "Registrado" es un estado derivado (no del enum): el wizard se completó pero
  // sigue en Draft, sin enviar a evaluación. Igual que Proveedor.
  const REGISTERED_STYLE = { key: 'registered', bg: 'rgba(42, 138, 135, 0.18)', color: '#1d6664' }
  const statusStyle = (e: CarrierListItemResponse) =>
    e.registrationStatus === 0 && e.wizardCompletedAt
      ? REGISTERED_STYLE
      : STATUS_STYLES[e.registrationStatus] ?? STATUS_STYLES[0]!
  const statusLabel = (e: CarrierListItemResponse) => t(`transportistas.list.statusMap.${statusStyle(e).key}`)
  const personTypeLabel = (pt: string) =>
    t(`transportistas.list.personType.${pt === 'Juridica' ? 'juridica' : 'natural'}`)

  const excelColumns: ExcelColumn<CarrierListItemResponse>[] = [
    { header: t('transportistas.list.headers.empresa'), accessor: (e) => e.legalName },
    { header: t('transportistas.list.headers.ruc'), accessor: (e) => e.ruc },
    { header: t('transportistas.list.headers.tipo'), accessor: (e) => personTypeLabel(e.personType) },
    { header: t('transportistas.list.headers.conductores'), accessor: (e) => e.driversCount },
    { header: t('transportistas.list.headers.vehiculos'), accessor: (e) => e.trucksCount },
    { header: t('transportistas.nuevo.fields.phone'), accessor: (e) => e.mainPhone ?? '' },
    { header: t('transportistas.nuevo.fields.email'), accessor: (e) => e.mainEmail ?? '' },
    { header: t('transportistas.list.headers.estado'), accessor: (e) => statusLabel(e) },
  ]

  const getInitials = (company: CarrierListItemResponse) => {
    const words = company.legalName.split(/\s+/)
    if (words.length >= 2) {
      return `${words[0]![0]}${words[1]![0]}`.toUpperCase()
    }
    return (words[0]?.[0] ?? '').toUpperCase()
  }

  const handleDelete = async (empresa: CarrierListItemResponse) => {
    await companiesService.delete(empresa.id)
    refresh()
  }

  const handleSubmitReview = async (empresa: CarrierListItemResponse) => {
    try {
      await transportistasService.submitForReview(empresa.id)
      toaster.success({ description: t('transportistas.nuevo.toasts.submitted') })
      refresh()
    } catch (e) {
      toaster.error({ description: e instanceof ApiError ? getErrorMessage(e.errorCode, e.message) : t('transportistas.evaluar.error') })
    }
  }

  const rowActions = (empresa: CarrierListItemResponse) => {
    // 0=Borrador/Registrado · 1=En Evaluación · 2=Observado · 3=Aprobado
    const isRegistered = empresa.registrationStatus === 0 && Boolean(empresa.wizardCompletedAt)
    const isObserved = empresa.registrationStatus === 2
    const inReview = empresa.registrationStatus === 1

    const actions: { label: string; onClick: () => void; color?: string }[] = [
      { label: t('transportistas.list.actions.view'), onClick: () => navigate(`/app/transportistas/${empresa.id}`) },
    ]
    // Evaluar: solo cuando ya fue enviado a evaluación (En Evaluación / Observado /
    // Aprobado). En Borrador/Registrado (status 0) aún no hay nada que evaluar.
    if (canView && empresa.registrationStatus !== 0) {
      actions.push({ label: t('transportistas.list.actions.evaluate'), onClick: () => navigate(`/app/transportistas/${empresa.id}/evaluar`) })
    }
    // Enviar a evaluación: disponible para Registrado u Observado (acción del registrador).
    if (canUpdate && (isRegistered || isObserved)) {
      actions.push({ label: t('transportistas.list.actions.sendToReview'), onClick: () => handleSubmitReview(empresa) })
    }
    // Editar: oculto mientras está En Evaluación (lo revisa el evaluador).
    if (canUpdate && !inReview) {
      actions.push({ label: t('transportistas.list.actions.edit'), onClick: () => navigate(`/app/transportistas/${empresa.id}`) })
    }
    if (canUpdate) {
      actions.push({ label: t('transportistas.list.actions.delete'), onClick: () => handleDelete(empresa), color: '#c8442f' })
    }
    return actions
  }

  // ── Helpers de presentación (funciones, no componentes, para evitar recrearlos en cada render) ──
  const renderStatusBadge = (e: CarrierListItemResponse) => {
    const s = statusStyle(e)
    return (
      <Box
        display="inline-block"
        px={2}
        py={0.5}
        borderRadius="md"
        fontSize="10px"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="0.05em"
        bg={s.bg}
        color={s.color}
      >
        {statusLabel(e)}
      </Box>
    )
  }

  const renderTypeBadge = (pt: string) => (
    <Box
      display="inline-block"
      px={2}
      py={0.5}
      borderRadius="md"
      fontSize="11px"
      fontWeight="600"
      bg={colors.bgHover}
      color={colors.text}
    >
      {personTypeLabel(pt)}
    </Box>
  )

  const renderCountBadge = (value: number) => (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="24px"
      px={2}
      py={0.5}
      borderRadius="full"
      fontSize="xs"
      fontWeight="600"
      bg={colors.bgHover}
      color={colors.text}
    >
      {value}
    </Box>
  )

  const renderAvatar = (company: CarrierListItemResponse) => (
    <Box
      width="36px"
      height="36px"
      borderRadius="full"
      bg={`linear-gradient(135deg, ${brand.green} 0%, ${brand.gold} 100%)`}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Text fontSize="xs" fontWeight="bold" color="white">
        {getInitials(company)}
      </Text>
    </Box>
  )

  // ── Vista de tarjetas ──
  const renderCards = (items: CarrierListItemResponse[]) => (
    <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
      {items.map((empresa, i) => (
        <MotionBox
          key={empresa.id}
          p={4}
          bg={colors.bgPanel}
          border="1px solid"
          borderColor={colors.border}
          borderRadius="xl"
          cursor="pointer"
          onClick={() => navigate(`/app/transportistas/${empresa.id}`)}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: i * 0.04 }}
          _hover={{ borderColor: brand.green, boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}
        >
          <Flex justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Flex alignItems="center" gap={3} minW={0}>
              {renderAvatar(empresa)}
              <Box minW={0}>
                <Text fontSize="sm" fontWeight="600" color={colors.text} wordBreak="break-word">
                  {empresa.legalName}
                </Text>
                {empresa.commercialName && (
                  <Text fontSize="xs" color={colors.textMuted}>
                    {empresa.commercialName}
                  </Text>
                )}
              </Box>
            </Flex>
            {renderStatusBadge(empresa)}
          </Flex>

          <Text fontFamily="monospace" fontSize="sm" color={colors.textMuted} mt={3}>
            {empresa.ruc}
          </Text>

          <Flex gap={4} mt={3} alignItems="center" flexWrap="wrap">
            {renderTypeBadge(empresa.personType)}
            <Flex alignItems="center" gap={1.5}>
              <Text fontSize="xs" color={colors.textMuted}>
                {t('transportistas.list.headers.conductores')}
              </Text>
              {renderCountBadge(empresa.driversCount)}
            </Flex>
            <Flex alignItems="center" gap={1.5}>
              <Text fontSize="xs" color={colors.textMuted}>
                {t('transportistas.list.headers.vehiculos')}
              </Text>
              {renderCountBadge(empresa.trucksCount)}
            </Flex>
          </Flex>
        </MotionBox>
      ))}
    </SimpleGrid>
  )

  // ── Toggle tabla/tarjetas ──
  const renderViewToggle = () => (
    <Flex gap={1} bg={colors.bgHover} borderRadius="lg" p={1}>
      {(
        [
          { mode: 'table' as const, icon: <LuList size={16} /> },
          { mode: 'cards' as const, icon: <LuLayoutGrid size={16} /> },
        ]
      ).map(({ mode, icon }) => (
        <Box
          key={mode}
          as="button"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="30px"
          height="28px"
          borderRadius="md"
          cursor="pointer"
          bg={viewMode === mode ? colors.bgPanel : 'transparent'}
          color={viewMode === mode ? brand.green : colors.textMuted}
          boxShadow={viewMode === mode ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'}
          onClick={() => setViewMode(mode)}
        >
          {icon}
        </Box>
      ))}
    </Flex>
  )

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title={t('transportistas.list.title')}
        subtitle={t('transportistas.list.subtitle')}
        buttonLabel={canCreate ? t('transportistas.list.newButton') : undefined}
        onButtonClick={canCreate ? () => setShowConsent(true) : undefined}
        buttonIcon={<LuTruck size={18} />}
        colors={colors}
      />

      <DataList<CarrierListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('transportistas.list.searchPlaceholder')}
        onExport={() => exportToExcel(empresas, excelColumns, 'transportistas')}
        toolbarExtra={
          <Flex gap={2} alignItems="center">
            {canUpdate && (
              <Box
                as="button"
                display="flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                borderRadius="md"
                fontSize="sm"
                fontWeight="500"
                color={colors.textMuted}
                cursor="pointer"
                _hover={{ bg: colors.bgHover, color: colors.text }}
                onClick={() => navigate('/app/transportistas/regularizar')}
              >
                {t('transportistas.list.regularize')}
              </Box>
            )}
            {renderViewToggle()}
          </Flex>
        }
        viewMode={viewMode}
        renderCards={renderCards}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="transportistas"
        brandColor={brand.green}
        headers={[
          t('transportistas.list.headers.empresa'),
          t('transportistas.list.headers.ruc'),
          t('transportistas.list.headers.tipo'),
          t('transportistas.list.headers.conductores'),
          t('transportistas.list.headers.vehiculos'),
          t('transportistas.list.headers.estado'),
        ]}
        data={empresas}
        loading={loading}
        error={error}
        emptyMessage={t('transportistas.list.emptyMessage')}
        loadingMessage={t('transportistas.list.loadingMessage')}
        colors={colors}
        keyExtractor={(e) => e.id}
        renderRow={(empresa) => (
          <>
            {/* Empresa */}
            <DataTable.Cell>
              <Flex alignItems="center" gap={3}>
                {renderAvatar(empresa)}
                <Box>
                  <Text fontSize="sm" fontWeight="500" color={colors.text} wordBreak="break-word">
                    {empresa.legalName}
                  </Text>
                  {empresa.commercialName && (
                    <Text fontSize="xs" color={colors.textMuted}>
                      {empresa.commercialName}
                    </Text>
                  )}
                </Box>
              </Flex>
            </DataTable.Cell>

            {/* RUC */}
            <DataTable.Cell>
              <Text fontFamily="monospace" fontSize="sm" color={colors.text}>
                {empresa.ruc}
              </Text>
            </DataTable.Cell>

            {/* Tipo */}
            <DataTable.Cell>{renderTypeBadge(empresa.personType)}</DataTable.Cell>

            {/* Conductores */}
            <DataTable.Cell>{renderCountBadge(empresa.driversCount)}</DataTable.Cell>

            {/* Vehículos */}
            <DataTable.Cell>{renderCountBadge(empresa.trucksCount)}</DataTable.Cell>

            {/* Estado */}
            <DataTable.Cell>{renderStatusBadge(empresa)}</DataTable.Cell>
          </>
        )}
        actions={rowActions}
      />

      <ConsentModal
        open={showConsent}
        onClose={() => setShowConsent(false)}
        onAccept={() => {
          setShowConsent(false)
          navigate('/app/transportistas/nuevo')
        }}
      />
    </VStack>
  )
}
